import { useCallback, useEffect, useRef, useState } from 'react';
import type { Settings } from '../core/settings';
import type { Interpretation, SidHeader } from '../core/types';
import type { CaptureSummary, RegisterWrite, WorkerRequest, WorkerResponse } from '../worker/protocol';

export interface SessionFile {
  id: string;
  name: string;
  header?: SidHeader;
  error?: string;
}

export interface ConversionResult {
  fileId: string;
  interpretation: Interpretation;
  capture: CaptureSummary;
  timings: { captureMs: number; interpretMs: number; cached: boolean };
}

export type BusyStage = 'idle' | 'capturing' | 'interpreting';

let nextId = 1;

/** Owns the conversion worker. Only the latest requested conversion is ever run. */
export function useConverter() {
  const workerRef = useRef<Worker | null>(null);
  const [files, setFiles] = useState<SessionFile[]>([]);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<BusyStage>('idle');
  const inflight = useRef<number | null>(null);
  const pending = useRef<{ fileId: string; settings: Settings } | null>(null);
  const reqSeq = useRef(0);
  const writeWaiters = useRef(new Map<number, (r: { rows: RegisterWrite[]; totalTicks: number }) => void>());

  const send = useCallback((msg: WorkerRequest, transfer: Transferable[] = []) => {
    workerRef.current?.postMessage(msg, transfer);
  }, []);

  const dispatchConvert = useCallback(() => {
    const p = pending.current;
    if (!p || inflight.current !== null) return;
    pending.current = null;
    const reqId = ++reqSeq.current;
    inflight.current = reqId;
    setBusy('interpreting');
    send({ type: 'convert', reqId, fileId: p.fileId, settings: p.settings });
  }, [send]);

  useEffect(() => {
    const w = new Worker(new URL('../worker/convert.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = w;
    w.onmessage = (ev: MessageEvent<WorkerResponse>) => {
      const m = ev.data;
      switch (m.type) {
        case 'loaded':
          setFiles((fs) => fs.map((f) => (f.id === m.fileId ? { ...f, header: m.header } : f)));
          break;
        case 'loadError':
          setFiles((fs) => fs.map((f) => (f.id === m.fileId ? { ...f, error: m.message } : f)));
          break;
        case 'progress':
          setBusy(m.stage);
          break;
        case 'result':
          if (m.reqId === inflight.current) inflight.current = null;
          setResult({
            fileId: m.fileId,
            interpretation: m.interpretation,
            capture: m.capture,
            timings: m.timings,
          });
          setError(null);
          setBusy('idle');
          dispatchConvert();
          break;
        case 'error':
          if (m.reqId === inflight.current) inflight.current = null;
          setError(m.message);
          setResult(null);
          setBusy('idle');
          dispatchConvert();
          break;
        case 'writes': {
          const cb = writeWaiters.current.get(m.reqId);
          writeWaiters.current.delete(m.reqId);
          cb?.({ rows: m.rows, totalTicks: m.totalTicks });
          break;
        }
      }
    };
    w.onerror = (e) => {
      setError(`The conversion worker crashed: ${e.message}`);
      setBusy('idle');
      inflight.current = null;
    };
    return () => {
      w.terminate();
      workerRef.current = null;
    };
  }, [dispatchConvert]);

  const addFiles = useCallback(
    async (items: { name: string; bytes: ArrayBuffer }[]): Promise<string[]> => {
      const ids: string[] = [];
      for (const it of items) {
        const id = `f${nextId++}`;
        ids.push(id);
        setFiles((fs) => [...fs, { id, name: it.name }]);
        send({ type: 'load', fileId: id, name: it.name, bytes: it.bytes }, [it.bytes]);
      }
      return ids;
    },
    [send],
  );

  const removeFile = useCallback(
    (id: string) => {
      setFiles((fs) => fs.filter((f) => f.id !== id));
      send({ type: 'unload', fileId: id });
      setResult((r) => (r?.fileId === id ? null : r));
    },
    [send],
  );

  const convert = useCallback(
    (fileId: string, settings: Settings) => {
      pending.current = { fileId, settings };
      dispatchConvert();
    },
    [dispatchConvert],
  );

  const requestWrites = useCallback(
    (fileId: string, fromTick: number, count: number) =>
      new Promise<{ rows: RegisterWrite[]; totalTicks: number }>((resolve) => {
        const reqId = ++reqSeq.current;
        writeWaiters.current.set(reqId, resolve);
        send({ type: 'writes', reqId, fileId, fromTick, count });
      }),
    [send],
  );

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { files, result, error, busy, addFiles, removeFile, convert, requestWrites, clearResult };
}
